import type { UIMessage } from '@ai-sdk/react'
import type { ChatMessage, ChatSnapshot, ChatToolProgress } from '@plivo/chat-client'
import type { Part } from '../utils'

export type ToolPart = Extract<Part, { type: `tool-${string}` }>

export interface OverlayMessage {
  // The rendered message this one follows; null places it first.
  afterId: string | null
  message: UIMessage
}

export interface BuddyFlowView {
  snapshot: ChatSnapshot
  // Tool parts per turn, keyed by the id of the customer message that started the turn.
  // The SDK empties `progress` when a turn completes, so these are captured as they arrive.
  toolsByTurn: ReadonlyMap<string, ToolPart[]>
  inFlightTurnId?: string
  // The customer's message shown at once, until the SDK holds its own copy (same id).
  pendingUser?: UIMessage
  overlay: readonly OverlayMessage[]
  hiddenIds: ReadonlySet<string>
  // When each message id first appeared, partial or final. A turn's final messages can arrive
  // in a different order than they streamed in, and their stored timestamps tie.
  firstSeen?: ReadonlyMap<string, number>
}

const TOOL_STATES = {
  started: 'input-available',
  completed: 'output-available',
  failed: 'output-error',
} as const

// Progress is an append-only log per turn (started, any label updates, then completed or
// failed), so each tool call collapses to its latest entry, kept in the order the calls began.
export const progressToToolParts = (progress: readonly ChatToolProgress[]): ToolPart[] => {
  const latest = new Map<string, ChatToolProgress>()
  const entryCounts = new Map<string, number>()
  for (const entry of progress) {
    const key = entry.toolCallId ?? entry.id
    const seen = latest.get(key)
    if (!seen || entry.sequence >= seen.sequence) latest.set(key, entry)
    entryCounts.set(key, (entryCounts.get(key) ?? 0) + 1)
  }
  return [...latest.entries()].map(([toolCallId, entry]) => {
    const state = TOOL_STATES[entry.state]
    // A running tool that streamed a new label shows it in place of its fixed wording.
    const streamedLabel = entry.state === 'started' && (entryCounts.get(toolCallId) ?? 0) > 1
    return {
      type: `tool-${entry.id}`,
      toolCallId,
      state,
      input: {},
      ...(streamedLabel ? { title: entry.label } : {}),
      ...(state === 'output-available' ? { output: {} } : {}),
      ...(state === 'output-error' ? { errorText: '' } : {}),
    } as ToolPart
  })
}

type TextState = 'streaming' | 'done'

const textPart = (text: string, state: TextState = 'done'): Part => ({ type: 'text', text, state })

const fileParts = (message: ChatMessage): Part[] =>
  'files' in message
    ? message.files.map((file) => ({
        type: 'file' as const,
        mediaType: file.mimeType,
        filename: file.name,
        url: file.url,
      }))
    : []

// Text a person reads for any message kind. The Buddy flow only sends text and AI answers;
// the rest fall back to their visible wording rather than disappearing.
const messageText = (message: ChatMessage): string => {
  switch (message.kind) {
    case 'text':
    case 'ai_answer':
      return message.text
    case 'buttons':
    case 'menu_buttons':
      return [message.title, message.description].filter(Boolean).join('\n\n')
    case 'question':
      return message.question.prompt
    case 'carousel':
    case 'multi_product':
      return message.carouselMessage ?? ''
    case 'file':
    case 'image':
    case 'video':
    case 'audio':
    case 'document':
      return message.caption ?? ''
    default:
      return ''
  }
}

const toUserMessage = (message: ChatMessage): UIMessage | null => {
  const text = messageText(message)
  const parts = [...(text ? [textPart(text)] : []), ...fileParts(message)]
  return parts.length ? { id: message.id, role: 'user', parts } : null
}

const sourceParts = (message: ChatMessage): Part[] =>
  message.kind === 'ai_answer'
    ? message.sources.map((source) => ({
        type: 'source-url' as const,
        sourceId: source.id,
        url: source.url,
        title: source.title,
      }))
    : []

export const replyId = (turnId: string): string => `reply-${turnId}`

// Everything Buddy says in one turn. The flow can answer in several messages (a line before
// its tools run, then the answer), but the panel has always shown a turn as one bubble.
interface Reply {
  id: string
  tools: ToolPart[]
  messages: ChatMessage[]
}

interface StreamingText {
  id: string
  text: string
}

// The order the customer saw them stream in. Restored messages have no record and keep theirs.
const inSeenOrder = <T extends { id: string }>(
  items: readonly T[],
  firstSeen: ReadonlyMap<string, number> | undefined,
): readonly T[] =>
  firstSeen && items.every((item) => firstSeen.has(item.id))
    ? [...items].sort(
        (left, right) => (firstSeen.get(left.id) ?? 0) - (firstSeen.get(right.id) ?? 0),
      )
    : items

const renderReply = (
  reply: Reply,
  partials: readonly StreamingText[],
  textState: TextState,
  firstSeen: ReadonlyMap<string, number> | undefined,
): UIMessage | null => {
  const messages = inSeenOrder(reply.messages, firstSeen)
  const settled = messages.map((message) => ({ id: message.id, text: messageText(message) }))
  const texts = settled.map((piece) => piece.text)
  // A partial the server did not tag with its final message's id outlives that message.
  const unsettled = partials.filter((partial) => !texts.includes(partial.text))
  const text = inSeenOrder([...settled, ...unsettled], firstSeen)
    .map((piece) => piece.text)
    .filter(Boolean)
    .join('\n\n')
  const parts = [
    ...reply.tools,
    ...(text ? [textPart(text, textState)] : []),
    ...messages.flatMap(fileParts),
    ...messages.flatMap(sourceParts),
  ]
  return parts.length ? { id: reply.id, role: 'assistant', parts } : null
}

// The SDK does not keep its list in time order: a restored transcript can hold the answer
// before the question that settled after it. Stable, so equal timestamps keep SDK order.
const chronological = (messages: readonly ChatMessage[]): ChatMessage[] =>
  messages
    .map((message, index) => ({ message, index, at: Date.parse(message.createdAt) }))
    .sort((left, right) =>
      Number.isFinite(left.at) && Number.isFinite(right.at) && left.at !== right.at
        ? left.at - right.at
        : left.index - right.index,
    )
    .map(({ message }) => message)

const isRateLimited = (message: ChatMessage): boolean =>
  message.sender === 'customer' && message.sendDisposition === 'rejected_rate_limited'

export const toBuddyMessages = (view: BuddyFlowView): UIMessage[] => {
  const { snapshot, toolsByTurn, inFlightTurnId, pendingUser, overlay, hiddenIds, firstSeen } = view
  const out: UIMessage[] = []
  const placed = new Set<OverlayMessage>()
  const placeAfter = (id: string | null) => {
    for (const entry of overlay) {
      if (entry.afterId !== id || placed.has(entry)) continue
      placed.add(entry)
      if (hiddenIds.has(entry.message.id)) continue
      out.push(entry.message)
      placeAfter(entry.message.id)
    }
  }
  const push = (message: UIMessage | null) => {
    if (!message || hiddenIds.has(message.id)) return
    out.push(message)
    placeAfter(message.id)
  }
  placeAfter(null)

  let turnId: string | undefined
  let reply: Reply | null = null
  // A message Buddy sends before any question (a greeting) has no turn and keeps its own id.
  const openReply = (firstId: string): Reply => ({
    id: turnId ? replyId(turnId) : firstId,
    tools: turnId ? (toolsByTurn.get(turnId) ?? []) : [],
    messages: [],
  })
  const closeReply = (partials: readonly StreamingText[] = [], textState: TextState = 'done') => {
    if (reply) push(renderReply(reply, partials, textState, firstSeen))
    reply = null
  }
  const startTurn = (id: string, message: UIMessage | null) => {
    closeReply()
    turnId = id
    push(message)
  }

  for (const message of chronological(snapshot.messages)) {
    if (message.kind === 'lifecycle' || isRateLimited(message)) continue
    if (message.sender === 'customer') {
      startTurn(message.id, toUserMessage(message))
      continue
    }
    reply ??= openReply(message.id)
    reply.messages.push(message)
  }
  if (pendingUser && !snapshot.messages.some((message) => message.id === pendingUser.id)) {
    startTurn(pendingUser.id, pendingUser)
  }

  // The running turn also shows its tools so far and any text still arriving. The panel shows
  // its working indicator only under text that is not live, so the text stays live except
  // while tools run. Status cannot carry this: it must stay busy until the stream closes,
  // because the server holds the conversation's lock until then.
  const running = inFlightTurnId !== undefined && turnId === inFlightTurnId
  if (running) reply ??= openReply(inFlightTurnId)
  closeReply(
    running && snapshot.aiActivity !== 'idle' ? snapshot.partialMessages : [],
    running && snapshot.aiActivity !== 'working' ? 'streaming' : 'done',
  )
  // A local message whose anchor is gone (hidden, or an in-flight reply that settled) goes last.
  for (const entry of overlay) {
    if (placed.has(entry)) continue
    placed.add(entry)
    if (hiddenIds.has(entry.message.id)) continue
    out.push(entry.message)
    placeAfter(entry.message.id)
  }
  return out
}
